/**
 * SplashScreenV2 — redesigned loading screen (Lovable "spaceman-mission-control"
 * design), ported to plain React + CSS so it drops into the existing app with
 * no new dependencies. Same props contract as the old SplashScreen:
 *
 *   loadingComplete  bool — progress holds at 96% until true
 *   onComplete       fn() — called after the exit animation
 */
import { useEffect, useRef, useState } from 'react';
import earthTexture from '../../assets/earth-texture.jpg';
import { createOrbitScene } from './orbitCanvas';
import './SplashScreenV2.css';

const BOOT_LINES = [
  'BOOTING CORE_KERNEL v2.0.4…',
  'INITIALISING SYSTEMS…',
  'ESTABLISHING UPLINK…',
  'SYNCING ORBITAL DATA…',
  'ENCRYPTING DOWNLINK TUNNEL [AES-256]',
  'AUTHENTICATING MISSION COMMANDER…',
  'SYSTEM ONLINE',
];
const MIN_DURATION = 3600; // ms — long enough to read the boot sequence

const pad = n => String(n).padStart(2, '0');
const BRACKETS = ['tl', 'tr', 'bl', 'br'];

export default function SplashScreenV2({ loadingComplete, onComplete }) {
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const startRef = useRef(Date.now());
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const [progress, setProgress] = useState(0);
  const [typed, setTyped] = useState('');
  const [score, setScore] = useState(0);
  const [ripples, setRipples] = useState([]);
  const [met, setMet] = useState(0);
  const [exiting, setExiting] = useState(false);

  const done = progress >= 100;
  const lineIndex = done
    ? BOOT_LINES.length - 1
    : Math.min(BOOT_LINES.length - 2, Math.floor((progress / 100) * (BOOT_LINES.length - 1)));
  const line = BOOT_LINES[lineIndex];

  // Pin the page background to deep space while the splash is up, so the
  // exit fade never reveals a light theme's body colour (the "white flash").
  useEffect(() => {
    const html = document.documentElement;
    document.body.style.overflow = 'hidden';
    html.classList.add('spl2-booting');
    return () => {
      document.body.style.overflow = '';
      html.classList.remove('spl2-booting');
    };
  }, []);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!canvasRef.current) return undefined;
    sceneRef.current = createOrbitScene(canvasRef.current, earthTexture, reduced);
    return () => sceneRef.current?.destroy();
  }, []);

  // Progress: time-based, holds at 96% until loadingComplete
  useEffect(() => {
    const id = setInterval(() => {
      const t = (Date.now() - startRef.current) / MIN_DURATION;
      const eased = Math.min(1, 1 - Math.pow(1 - Math.min(t, 1), 2));
      const cap = loadingComplete ? 100 : 96;
      setProgress(p => Math.max(p, Math.min(cap, Math.round(eased * 100))));
      setMet(Date.now() - startRef.current);
    }, 60);
    return () => clearInterval(id);
  }, [loadingComplete]);

  // Typewriter for the current boot line
  useEffect(() => {
    let i = 0;
    setTyped('');
    const id = setInterval(() => {
      i++;
      setTyped(line.slice(0, i));
      if (i >= line.length) clearInterval(id);
    }, 22);
    return () => clearInterval(id);
  }, [line]);

  // Exit: gentle globe zoom, then fade to deep space (no bright overlay flash)
  useEffect(() => {
    if (!done) return undefined;
    sceneRef.current?.setScale(1.06);
    const t1 = setTimeout(() => setExiting(true), 700);
    const t2 = setTimeout(() => onCompleteRef.current?.(), 1100);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [done]);

  function handleClick(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    sceneRef.current?.launch(x, y);
    setScore(s => s + 1);
    const id = Date.now() + Math.random();
    setRipples(r => [...r, { id, x, y }]);
    setTimeout(() => setRipples(r => r.filter(q => q.id !== id)), 650);
  }

  const s = Math.floor(met / 1000);
  const metStr = `${pad(Math.floor(s / 86400))}:${pad(Math.floor(s / 3600) % 24)}:${pad(Math.floor(s / 60) % 60)}:${pad(s % 60)}`;

  return (
    <div role="status" aria-label={`Loading Spacemancraft Satellite OS, ${progress}%`}
      onClick={handleClick} className={`spl2${exiting ? ' spl2--exit' : ''}`}>
      {/* 1. blueprint grid */}
      <div className="spl2-grid" />
      {/* 2. glow */}
      <div className="spl2-glow" />
      {/* 3. globe + satellites */}
      <canvas ref={canvasRef} className="spl2-canvas" />

      {ripples.map(r => (
        <span key={r.id} className="spl2-ripple" style={{ left: r.x, top: r.y }} />
      ))}

      {/* 4. corner brackets */}
      {BRACKETS.map((c, i) => (
        <span key={c} className={`spl2-bracket spl2-bracket--${c}`} style={{ animationDelay: `${i * 90}ms` }} />
      ))}

      {/* 5. HUD top bar */}
      <div className="spl2-hud">
        <div className="spl2-hud-col">
          <span className="spl2-live">
            <span className="spl2-ping"><span /><span /></span>
            SYSTEM_LIVE
          </span>
          <span className="spl2-tx2">MET {metStr}</span>
        </div>
        <div className="spl2-hud-col spl2-hud-col--right">
          <span className="spl2-tx3">AUTH_STATUS</span>
          <span className={done ? 'spl2-gn' : 'spl2-am'}>{done ? 'AUTHENTICATED' : 'AWAITING_PROTOCOL'}</span>
          <span className="spl2-tx2 spl2-score">SCORE <span className="spl2-ac2">{pad(score)}</span></span>
        </div>
      </div>

      {/* 6. branding */}
      <div className="spl2-brand">
        <img src="/logo-light.png" alt="Spacemancraft" className="spl2-logo spl2-rise" />
        <p className="spl2-sub spl2-rise" style={{ animationDelay: '150ms' }}>Orbital_Operating_System // v2.4.0</p>
        <p className="spl2-label spl2-tx3 spl2-rise" style={{ animationDelay: '250ms', marginTop: 8 }}>
          SATELLITE-CLASS OS · DEEP SPACE NETWORK
        </p>
        <div className="spl2-stats spl2-rise" style={{ animationDelay: '350ms' }}>
          <div className="spl2-stat" style={{ borderColor: 'var(--s2-ac)' }}>
            <div className="spl2-label spl2-tx3">DOWNLINK_RATE</div>
            <div className="spl2-stat-val">482.1 <span className="spl2-unit">GB/S</span></div>
          </div>
          <div className="spl2-stat" style={{ borderColor: 'var(--s2-gn)' }}>
            <div className="spl2-label spl2-tx3">SIGNAL_LOCK</div>
            <div className="spl2-stat-val spl2-gn">STABLE</div>
          </div>
        </div>
      </div>

      {/* 7 + 8. vectors, progress, boot terminal */}
      <div className="spl2-bottom">
        <div className="spl2-label spl2-tx2 spl2-vectors">
          <span>VEL <span className="spl2-tx">7.67 KM/S</span></span>
          <span>ALT <span className="spl2-tx">450.0 KM</span></span>
          <span>INC <span className="spl2-tx">97.4°</span></span>
        </div>
        <div className="spl2-bar"><div className="spl2-bar-fill" style={{ width: `${progress}%` }} /></div>
        <div className="spl2-term">
          <span className="spl2-term-line">
            <span className="spl2-tx3">&gt; </span>{typed}
            <span className="spl2-caret">▌</span>
            {done && <span className="spl2-gn" style={{ marginLeft: 12 }}>FIREBASE CONNECTED</span>}
          </span>
          <span className="spl2-tx2">LOADING… {pad(progress)}%</span>
        </div>
        <div className="spl2-label spl2-tx3 spl2-hint">CLICK TO LAUNCH SATELLITE</div>
      </div>
    </div>
  );
}
